import { useTranslation } from 'react-i18next';
import type { UseMessagesResult } from '@/api';
import type { MessageBucket, PlayerMessage } from '@/domain/messages';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { EmptyState } from '../common/empty-state';
import { Spinner } from '../common/spinner';
import { Toast } from '../common/toast';
import { MessageListRow } from './MessageListRow';

export interface MessagesPanelProps {
  messages: UseMessagesResult;
  onSelectMessage?: (message: PlayerMessage) => void;
  className?: string;
}

const BUCKET_CHIPS: {
  value: MessageBucket;
  labelKey: string;
  defaultLabel: string;
}[] = [
  { value: 'all', labelKey: 'All', defaultLabel: 'All' },
  { value: 'new', labelKey: 'New', defaultLabel: 'New' },
  { value: 'read', labelKey: 'Read', defaultLabel: 'Read' },
];

function FilterChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'rounded-md px-3 py-2 text-[13px] whitespace-nowrap transition-colors',
        selected
          ? 'bg-action-primary font-bold text-text-on-action'
          : 'bg-bg-elevated font-normal text-text-secondary hover:text-text-primary',
      )}
    >
      {label}
    </button>
  );
}

/**
 * Profile messages body: All/New/Read chips and inbox list rows.
 */
export function MessagesPanel({ messages, onSelectMessage, className }: MessagesPanelProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const { bucket, setBucket, rows, isLoading, listError } = messages;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div
        role="group"
        aria-label={label('Message status', 'Message status')}
        className="flex flex-wrap gap-2"
      >
        {BUCKET_CHIPS.map((chip) => (
          <FilterChip
            key={chip.value}
            label={label(chip.labelKey, chip.defaultLabel)}
            selected={bucket === chip.value}
            onClick={() => setBucket(chip.value)}
          />
        ))}
      </div>

      {listError ? (
        <Toast tone="danger">
          {label('Could not load messages', 'Could not load messages')}
          {listError.message ? `: ${listError.message}` : ''}
        </Toast>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={label('Loading messages', 'Loading messages')} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="messages"
          title={label('No messages', 'No messages')}
          description={
            bucket === 'new'
              ? label('You have no new messages.', 'You have no new messages.')
              : bucket === 'read'
                ? label('No read messages for this filter.', 'No read messages for this filter.')
                : label('Your inbox is empty.', 'Your inbox is empty.')
          }
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((message) => (
            <li key={message.Id}>
              <MessageListRow message={message} onSelect={onSelectMessage} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
